import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';

// Visor de errores de cliente (client_errors) para dirección — observabilidad.
// La colección es read:false en las reglas; se consulta vía getClientErrors.
export default function ErroresAdminView() {
  const [data, setData]     = useState({ errores: [], resumen: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [abierto, setAbierto] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/getClientErrors?limit=200', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al cargar');
      setData({ errores: d.errores || [], resumen: d.resumen || [] });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const fecha = s => (s ? new Date(s).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Errores de la aplicación</h1>
          <p className="text-sm text-gray-500">{data.errores.length} eventos recientes · capturados por el ErrorBoundary y los handlers globales</p>
        </div>
        <button onClick={cargar} disabled={loading}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl">
          {loading ? '…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

      {!loading && data.errores.length === 0 && !error && (
        <p className="text-sm text-gray-400 text-center py-12">🎉 Sin errores registrados</p>
      )}

      {data.resumen.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm p-4">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Más frecuentes</h3>
          <div className="space-y-1.5">
            {data.resumen.map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="text-xs font-bold bg-red-100 text-red-700 rounded-full px-2 py-0.5 flex-shrink-0">{r.count}×</span>
                <span className="flex-1 min-w-0 truncate text-gray-700">{r.mensaje}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{fecha(r.ultima)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.errores.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm divide-y divide-gray-50">
          {data.errores.map(e => (
            <div key={e.id} className="px-4 py-3">
              <button onClick={() => setAbierto(a => a === e.id ? null : e.id)} className="w-full text-left">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{e.source || 'manual'}</span>
                  <span className="text-sm font-medium text-gray-800 flex-1 min-w-0 truncate">{e.message}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{fecha(e.createdAt)}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {[e.role, e.email, e.url].filter(Boolean).join(' · ')}
                </p>
              </button>
              {abierto === e.id && (
                <pre className="mt-2 text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-auto max-h-64 whitespace-pre-wrap">
                  {e.stack || '(sin stack)'}
                  {e.componentStack ? `\n\n— componentStack —\n${e.componentStack}` : ''}
                  {e.context ? `\n\ncontexto: ${e.context}` : ''}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
